
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAttendanceNotificationSettings } from '@/hooks/useAttendanceNotificationSettings';
import { toast } from 'sonner';
import { AlertCircle, Bell } from 'lucide-react';

export const AttendanceNotificationSettings: React.FC = () => {
  const { 
    thresholds, 
    setThresholds, 
    isLoading, 
    error 
  } = useAttendanceNotificationSettings();
  
  const [absenceThreshold, setAbsenceThreshold] = useState(thresholds.absenceThreshold);
  const [latenessThreshold, setLatenessThreshold] = useState(thresholds.latenessThreshold);

  const handleSave = async () => {
    const success = await setThresholds({
      absenceThreshold,
      latenessThreshold
    });
    
    if (success) {
      toast.success('Attendance notification settings saved');
    } else {
      toast.error('Failed to save attendance notification settings');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bell className="h-5 w-5 mr-2" />
          Attendance Notification Thresholds
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center bg-red-100 p-3 rounded text-red-800 mb-4">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="absence-threshold">Absence Alert Threshold</Label>
          <div className="flex items-center">
            <Input
              id="absence-threshold"
              type="number"
              min={1}
              max={10}
              value={absenceThreshold}
              onChange={(e) => setAbsenceThreshold(parseInt(e.target.value) || 3)}
              className="w-20 mr-3"
            />
            <span className="text-sm text-muted-foreground">
              Alert after this many absences
            </span>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lateness-threshold">Lateness Alert Threshold</Label>
          <div className="flex items-center">
            <Input
              id="lateness-threshold"
              type="number"
              min={1}
              max={10}
              value={latenessThreshold}
              onChange={(e) => setLatenessThreshold(parseInt(e.target.value) || 4)}
              className="w-20 mr-3"
            />
            <span className="text-sm text-muted-foreground">
              Alert after this many late arrivals
            </span>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleSave} 
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? 'Saving...' : 'Save Thresholds'}
        </Button>
      </CardFooter>
    </Card>
  );
};
